import { useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import type { PurchaseOrderFile } from "@/api/purchase";
import { useBestandBuchen, useBestandBuchenVorschau } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PinConfirmDialog } from "@/components/purchase/PinConfirmDialog";
import { AMPEL_KLASSE, AMPEL_TEXT, SPERRE_TEXT, grundText, herkunftText } from "./bestand-buchen";

/**
 * Plan 2026-10-19 (a/a′): „Bestand buchen“ — der Container wird eingelagert (nur Plattform, kein Write auf
 * den Bestand, keine Übertragung). Die Vorschau zeigt je Artikel den Alt-Bestand-Vorschlag mit Ampel; gelbe
 * und rote Vorschläge bestätigt der Bediener später im Misch-EK-Dialog.
 */
export function BestandBuchenDialog({
  shipmentId, order, onClose,
}: { shipmentId: number | null; order: PurchaseOrderFile; onClose: () => void }) {
  const { data, isLoading, isError } = useBestandBuchenVorschau(shipmentId);
  const buchen = useBestandBuchen(shipmentId ?? 0);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const artikel = (orderItemIds: number[]) => {
    const oi = order.items.find((i) => orderItemIds.includes(i.id));
    return oi?.merchant_sku ?? oi?.sku ?? `Pos. ${orderItemIds.join(", ")}`;
  };
  const positionen = data?.positionen ?? [];
  const sperre = data?.sperre ?? null;

  const handleConfirmPin = async (pin: string) => {
    if (!shipmentId) return;
    setPinError(null);
    try {
      await buchen.mutateAsync({ pin });
      setPinOpen(false);
      toast.success("Bestand gebucht — Container eingelagert");
      onClose();
    } catch (e: unknown) {
      setPinError(e instanceof ApiError ? e.message : "Fehler beim Buchen");
    }
  };

  return (
    <>
      <Dialog open={shipmentId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bestand buchen</DialogTitle>
            <DialogDescription>
              Der Container wird nur in der Stahlia-Plattform eingelagert. Es werden keine Mengen oder Preise an DreamRobot übertragen.
              Je Artikel steht der Bestand vor dieser Lieferung — er wird später im Misch-EK verrechnet.
            </DialogDescription>
          </DialogHeader>

          {sperre !== null ? (
            <div role="alert" className="rounded-md border border-warn/40 bg-warn-soft p-3 text-sm text-ink">{SPERRE_TEXT[sperre] ?? sperre}</div>
          ) : null}

          {isLoading ? (
            <div className="py-6 text-center text-sm text-ink-2">Vorschau laden…</div>
          ) : isError ? (
            <div className="py-6 text-center text-sm text-coral">Fehler beim Laden der Vorschau.</div>
          ) : positionen.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-2">Keine eingelagerten Positionen.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs text-ink-2">
                    <th className="py-2 pr-3">Artikel</th>
                    <th className="py-2 pr-3 text-right">Eingelagert</th>
                    <th className="py-2 pr-3 text-right">Bestand vorher</th>
                    <th className="py-2 pr-3">Herkunft</th>
                    <th className="py-2 pr-3">Ampel</th>
                  </tr>
                </thead>
                <tbody>
                  {positionen.map((p) => (
                    <tr key={p.stock_item_id} className="border-b border-line/50 align-top">
                      <td className="py-2 pr-3 font-medium">{artikel(p.order_item_ids)}</td>
                      <td className="py-2 pr-3 text-right">{p.eingelagert.toLocaleString("de-DE")}</td>
                      <td className="py-2 pr-3 text-right font-semibold">{p.alt_vorschlag.toLocaleString("de-DE")}</td>
                      <td className="py-2 pr-3 text-xs text-ink-2">
                        {herkunftText(p)}
                        {p.gruende.length > 0 ? <div>{p.gruende.map(grundText).join(" · ")}</div> : null}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${AMPEL_KLASSE[p.ampel]}`}>
                          {AMPEL_TEXT[p.ampel]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {positionen.some((p) => p.ampel !== "gruen") ? (
                <p className="mt-2 text-xs text-ink-2">Gelbe und rote Vorschläge bestätigst oder änderst du im Misch-EK-Dialog.</p>
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Abbrechen</Button>
            <Button
              size="sm"
              disabled={buchen.isPending || isLoading || sperre !== null || positionen.length === 0}
              onClick={() => setPinOpen(true)}
            >
              Bestand buchen…
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PinConfirmDialog
        open={pinOpen}
        onOpenChange={setPinOpen}
        titel="Bestand buchen bestätigen"
        beschreibung="Bitte 6-stellige PIN eingeben, um den Container einzulagern."
        busy={buchen.isPending}
        errorText={pinError}
        onConfirm={handleConfirmPin}
      />
    </>
  );
}
